import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, user } from "@habits/powersync";

export interface UserPreferences {
  locale: string | null;
  global_theme_id: string | null;
  is_blur: number | null;
  follow_device_theme: number | null;
  font_family: string | null;
  /**
   * Storage key for the avatar (`avatars/{userId}/{hash}.webp`).
   *
   * It lives here and is intentionally not read from the session: the session
   * is served and cached by the API, so a newly uploaded photo would not appear
   * until the next refresh. Reading it from the local row —like the language,
   * font, and theme— makes the photo appear instantly and survive offline.
   */
  image: string | null;
}

/**
 * Shared Drizzle query builder for User preferences.
 */
export function getUserPreferencesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
      font_family: user.font_family,
      image: user.image,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User locale.
 */
export function getUserLocaleQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      locale: user.locale,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User font family preference.
 */
export function getUserFontFamilyQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      font_family: user.font_family,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}

/**
 * Shared Drizzle query builder for User theme settings.
 *
 * It ALWAYS filters by `userId`, just like the other `user` reads in this
 * module. Previously the filter was chosen in one branch and a guest was
 * left with bare `SELECT … FROM user LIMIT 1`: without `WHERE`, SQLite returns
 * the FIRST row it likes, which need not be the guest's. This is not a
 * correctness bug but a data leak between accounts: during a guest→real
 * upgrade, the local database downloads one `user` row per co-member of the
 * organization, so the guest ended up rendering ANOTHER person's theme.
 * Without `WHERE`, an empty result is impossible: someone always comes back.
 */
export function getUserThemeSettingsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      global_theme_id: user.global_theme_id,
      is_blur: user.is_blur,
      follow_device_theme: user.follow_device_theme,
    })
    .from(user)
    .where(eq(user.id, userId))
    .limit(1);
}
